import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  PageHeader,
  Skeleton,
  StatTile,
} from '@provenance/design';
import type { Dashboard as DashboardData } from '@provenance/shared';
import {
  ArrowUpRight,
  CheckCircle2,
  Clock,
  FileClock,
  Inbox,
  ScrollText,
  UserPlus,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCounts } from '../features/counts';
import { relative } from '../lib/format';
import { useSession } from '../lib/session';

/**
 * The dashboard.
 *
 * Spec §6 asks for "a real operational summary, not a placeholder", and the thing an
 * operator's day is actually organised around is the SLA: what is overdue, what is due
 * today, what is coming. So the ageing breakdown gets the largest tiles, and each one is
 * a link into the queue already filtered to that slice — a number you cannot act on is
 * decoration.
 */
export function Dashboard() {
  const { user } = useSession();
  const { data, isLoading } = useCounts();

  const greeting = `${timeOfDay()}, ${user?.firstName}`;

  return (
    <div className="flex flex-col gap-7">
      <PageHeader
        title={greeting}
        lede={
          data
            ? `${data.awaitingAction.total} request${data.awaitingAction.total === 1 ? '' : 's'} awaiting action. Median turnaround over the last 30 days is ${
                data.medianTurnaroundHours ? `${data.medianTurnaroundHours} hours` : 'not yet measurable'
              }.`
            : undefined
        }
        actions={
          <Button variant="primary" asChild trailingIcon={<ArrowUpRight className="size-3.5" />}>
            <Link to="/documents/pending">Open the queue</Link>
          </Button>
        }
      />

      {isLoading || !data ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-[104px]" />
          ))}
        </div>
      ) : (
        <>
          {/* SLA ageing. Overdue first and loudest, because that is the state that
              determines what an operator does next. */}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile
              label="Overdue"
              value={data.awaitingAction.overdue}
              tone={data.awaitingAction.overdue > 0 ? 'stop' : 'good'}
              sublabel={
                data.awaitingAction.overdue > 0
                  ? 'Past the requested date'
                  : 'Nothing past its requested date'
              }
            />
            <StatTile
              label="Due today"
              value={data.awaitingAction.dueToday}
              tone={data.awaitingAction.dueToday > 0 ? 'warn' : 'neutral'}
              sublabel="Requested for today"
            />
            <StatTile
              label="Due this week"
              value={data.awaitingAction.dueThisWeek}
              tone="clay"
              sublabel="Within the next seven days"
            />
            <StatTile
              label="No target date"
              value={data.awaitingAction.noTargetDate}
              tone="neutral"
              sublabel="No date was supplied"
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
            <div className="flex flex-col gap-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <QueueLink
                  to="/documents/pending"
                  icon={<Inbox className="size-4" />}
                  label="Pending"
                  value={data.awaitingAction.total}
                />
                <QueueLink
                  to="/documents/clarification"
                  icon={<FileClock className="size-4" />}
                  label="Awaiting clarification"
                  value={data.awaitingClarification}
                />
                <QueueLink
                  to="/registrations"
                  icon={<UserPlus className="size-4" />}
                  label="Registrations"
                  value={data.pendingRegistrations}
                />
              </div>

              <Card>
                <CardHeader
                  title="Issuance"
                  hint="Last 30 days"
                  description="Certificates issued per day."
                />
                <Throughput data={data.throughput} />
                <div className="mt-4 grid grid-cols-3 gap-4 border-t border-[var(--pv-line)] pt-4">
                  <Figure label="This month" value={data.issuedThisMonth} />
                  <Figure label="Last month" value={data.issuedLastMonth} />
                  <Figure
                    label="Median turnaround"
                    value={data.medianTurnaroundHours ? `${data.medianTurnaroundHours}h` : '—'}
                  />
                </div>
              </Card>
            </div>

            <Card>
              <CardHeader
                title="Recent activity"
                hint="Everything that happened"
                description="Every state change is recorded with who and when."
              />
              {data.recentActivity.length === 0 ? (
                <EmptyState
                  icon={<Clock className="size-5" />}
                  title="Nothing yet"
                  description="Activity appears here as requests arrive and are actioned."
                />
              ) : (
                <ol className="flex list-none flex-col gap-0 p-0">
                  {data.recentActivity.map((event) => (
                    <li
                      key={event.id}
                      className="flex gap-3 border-b border-[var(--pv-line)] py-2.5 last:border-b-0 last:pb-0"
                    >
                      <span
                        aria-hidden
                        className="mt-1.5 size-1.5 shrink-0 rounded-full bg-[var(--pv-leaf-deep)]"
                      />
                      <div className="min-w-0">
                        <p className="[font-size:13px] leading-snug">{event.summary}</p>
                        <p className="mt-0.5 [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                          {event.actorName ?? 'System'} · {relative(event.createdAt)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </div>

          {data.pendingModificationRequests > 0 && (
            <Card className="flex items-center gap-4">
              <ScrollText aria-hidden className="size-5 shrink-0 text-[var(--pv-clay-deep)]" />
              <p className="min-w-0 flex-1 [font-size:13.5px]">
                <strong className="font-semibold">
                  {data.pendingModificationRequests} certificate modification request
                  {data.pendingModificationRequests === 1 ? '' : 's'}
                </strong>{' '}
                <span className="text-[var(--pv-muted)]">
                  from exporters asking for a correction to an already-issued certificate.
                </span>
              </p>
              <Button variant="secondary" size="sm" asChild>
                <Link to="/modifications">Review</Link>
              </Button>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function QueueLink({
  to,
  icon,
  label,
  value,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-[var(--pv-radius-lg)] border border-[var(--pv-line)] bg-[var(--pv-card)] p-3.5 transition-shadow hover:shadow-[var(--pv-shadow-sm)]"
    >
      <span className="grid size-8 shrink-0 place-items-center rounded-[var(--pv-radius-sm)] bg-[var(--pv-brand-soft)] text-[var(--pv-ink)]">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="pv-num block [font-size:20px] font-bold leading-none">{value}</span>
        <span className="mt-1 block truncate [font-size:var(--pv-text-xs)] text-[var(--pv-muted)]">
          {label}
        </span>
      </span>
      <ArrowUpRight
        aria-hidden
        className="size-3.5 shrink-0 text-[var(--pv-faint)] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
      />
    </Link>
  );
}

function Figure({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="pv-eyebrow text-[var(--pv-faint)]">{label}</p>
      <p className="pv-num mt-1 [font-size:18px] font-bold leading-none">{value}</p>
    </div>
  );
}

/**
 * A 30-day issuance sparkline, drawn as inline SVG.
 *
 * No charting library: it is thirty bars with no axes, no legend and no interaction
 * beyond a tooltip, and a charting dependency for that would be 40KB to draw rectangles.
 */
function Throughput({ data }: { data: DashboardData['throughput'] }) {
  if (data.length === 0) {
    return (
      <p className="py-6 text-center [font-size:13px] text-[var(--pv-faint)]">
        No certificates issued in the last 30 days.
      </p>
    );
  }

  const max = Math.max(...data.map((d) => d.issued), 1);
  const total = data.reduce((sum, d) => sum + d.issued, 0);

  return (
    <figure className="m-0">
      <figcaption className="sr-only">
        Certificates issued per day over the last 30 days. {total} in total, peaking at {max} in a
        single day.
      </figcaption>
      <div className="flex h-24 items-end gap-[3px]" role="img" aria-label={`${total} certificates issued over 30 days`}>
        {data.map((point) => (
          <div
            key={point.date}
            title={`${point.date}: ${point.issued} issued`}
            style={{ height: `${Math.max(4, (point.issued / max) * 100)}%` }}
            className="min-w-[4px] flex-1 rounded-t-[3px] bg-[var(--pv-leaf)] transition-colors hover:bg-[var(--pv-leaf-deep)]"
          />
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
        <span>{data[0]?.date}</span>
        <span className="inline-flex items-center gap-1">
          <CheckCircle2 aria-hidden className="size-3" /> {total} issued
        </span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </figure>
  );
}

function timeOfDay(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}
